import * as React from 'react';
import { Spinner, SpinnerSize, Text, MessageBar, MessageBarType, Dropdown, IDropdownOption, ComboBox, IComboBox, IComboBoxOption } from '@fluentui/react';
import { Context } from '@micro-frontend-react/core/lib/Context';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';

import { PBTApiEndpoints, PBTImmigrationStatusOptions, PBTVisaTypeOptions, PBTWorkStateOptions, PBTLookupCategories } from '../../../Services/PBTConstants';
import { usePbtLookupOptions } from '../../../Services/PBT/usePbtLookupOptions';
import { caseDetailStyles } from './CaseDetail.styled';
import { GatedField, maskValue } from '../Gated/GatedField';
import { useRole } from '../../../Hooks/useFeature';
import { PBTDataStatus } from '../../../Interfaces/IPBTCase';
import { updateCaseInStore } from '../../../Pages/PBT/HRCDashboard/useHRCDashboard';

/** Capability path prefix for employee-data fields under the case-detail panel. */
const EMP_CAP = 'page.hrcDashboard/modal.caseDetail/tab.caseDetail.empData';

interface EmployeeDataTabProps {
  personnelNumber: string;
  pbtCaseId: number;
  gerWitCheckStatus?: string | null;
  /**
   * Bug 16442069 / 16442169: reports whether the case is blocked on required Employee Data
   * (i.e. at least one field is 'Blocking' — the same condition that renders the
   * "Workflow Blocked — Required Data Missing" banner). The parent CaseDetail uses this to hide
   * downstream tabs and mark the Data Gathering stepper node as blocked.
   */
  onBlockedChange?: (blocked: boolean) => void;
}

interface FieldCompleteness {
  [field: string]: 'Complete' | 'Warning' | 'Blocking';
}

interface EmployeeData {
  personnelNumber: string;
  employeeName: string;
  preferredName: string;
  homeAddress: string;
  homeState?: string;
  mostRecentHireDate: string;
  workLocation: string;
  workAddress: string;
  workState: string;
  immigrationStatus?: string;
  visaType?: string;
  sevenDayRule?: boolean;
  hrdpRetrievalSuccess: boolean;
  mimsRetrievalSuccess: boolean;
  mimsErrorMessage: string | null;
  retrievedAt: string;
  fieldCompleteness: FieldCompleteness;
}

function deriveDataStatus(fieldCompleteness: FieldCompleteness): PBTDataStatus {
  const statuses = Object.entries(fieldCompleteness)
    .filter(([field]) => field !== 'PersonalEmail')
    .map(([, status]) => status);

  if (statuses.some((status) => status === 'Blocking')) return PBTDataStatus.Blocked;
  if (statuses.some((status) => status === 'Warning')) return PBTDataStatus.ActionNeeded;
  return PBTDataStatus.Complete;
}

/** Human-readable messages for field issues */
const fieldMessages: Record<string, { blocking: string; warning: string; impact?: string }> = {
  WorkState: { blocking: 'Work State is missing — cannot determine 7-day rule or state-specific requirements.', warning: '', impact: 'Workflow cannot proceed without this field.' },
  HomeAddress: { blocking: 'Home Address retrieval failed.', warning: 'Home Address is not available — notification letter may be incomplete.', impact: 'Final letter may lack employee address.' },
  ImmigrationStatus: { blocking: 'Immigration Status retrieval failed.', warning: 'Immigration Status could not be determined. — Flow continues with manual-check-required flag.', impact: 'HRC must manually verify immigration status.' },
  VisaType: { blocking: 'Visa Type retrieval failed.', warning: 'Visa Type could not be determined.', impact: 'HRC must manually verify the visa type.' },
  EmployeeName: { blocking: 'Employee Name is missing — cannot generate termination documents.', warning: '' },
  WorkLocation: { blocking: 'Work Location is missing — cannot determine jurisdiction.', warning: '' },
};

// Manual-override presentation. Backend override field names drive the mapping: 'WorkState' stores
// a 2-letter USPS state code, 'ImmigrationStatus' stores the Yes/No Immigration Status, and 'VisaType'
// stores the detailed Visa Type label. Those three render as dropdowns; the remaining fields keep
// free-text entry. WorkState options are served from the database (dbo.LPMLookup, LookupCategory
// 'WorkState') via the lookup endpoint so the offered states can change as data without a code
// change/redeploy; PBTWorkStateOptions is the offline/failure fallback. WorkState renders as a
// searchable ComboBox (type-to-filter) because the list of states is long.
const overrideFieldLabels: Record<string, string> = {
  WorkState: 'Work State',
  HomeAddress: 'Home Address',
  ImmigrationStatus: 'Immigration Status',
  VisaType: 'Visa Type',
};

const overrideDropdownOptions: Record<string, readonly { key: string; text: string }[]> = {
  ImmigrationStatus: PBTImmigrationStatusOptions,
  VisaType: PBTVisaTypeOptions,
};

// Fields whose dropdown should be a searchable ComboBox (type-to-filter) rather than a plain
// Dropdown. WorkState has ~51 options, so type-ahead filtering is a much better experience.
const searchableOverrideFields = new Set<string>(['WorkState']);

export const EmployeeDataTab: React.FC<EmployeeDataTabProps> = ({
  personnelNumber,
  pbtCaseId,
  gerWitCheckStatus,
  onBlockedChange,
}) => {
  const { authClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);
  const isManager = useRole() === 'Manager';
  const [data, setData] = React.useState<EmployeeData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showOverrides, setShowOverrides] = React.useState(false);
  const [overrideValues, setOverrideValues] = React.useState<Record<string, string>>({});
  const [isRetrying, setIsRetrying] = React.useState(false);

  const getToken = React.useCallback(
    () => authClient.acquireToken(__SCOPES__),
    [authClient]
  );

  // WorkState dropdown options come from the database (dbo.LPMLookup, LookupCategory 'WorkState')
  // served via the lookup endpoint; PBTWorkStateOptions is the fallback while loading or if the
  // request fails. mapCodeToKey=true so the 2-letter code (e.g. "NY") is the stored key and the
  // full state name is the label.
  const { options: workStateOptions } = usePbtLookupOptions(
    PBTLookupCategories.WorkState,
    PBTWorkStateOptions as unknown as IDropdownOption[],
    true
  );

  const dropdownOptionsByField = React.useMemo<Record<string, readonly { key: string; text: string }[]>>(
    () => ({ ...overrideDropdownOptions, WorkState: workStateOptions as unknown as { key: string; text: string }[] }),
    [workStateOptions]
  );

  // Fetch employee data with overrides scoped to the case currently being viewed.
  const fetchData = React.useCallback(async () => {
    if (!personnelNumber) {
      setError('No personnel number available.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const res = await fetch(PBTApiEndpoints.EmployeeData(personnelNumber, pbtCaseId), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const json = await res.json();
      setData(json.data ?? null);
    } catch {
      setError("We're unable to fetch the employee's details right now. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [personnelNumber, pbtCaseId, getToken]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Bug 16442069 / 16442169: report the authoritative blocked state to the parent whenever the
  // fetched field completeness changes. Blocked === at least one 'Blocking' field (same condition
  // as the "Workflow Blocked — Required Data Missing" banner below).
  React.useEffect(() => {
    if (!onBlockedChange || !data) return;
    const fcLatest = data.fieldCompleteness || {};
    const blocked = Object.values(fcLatest).some((status) => status === 'Blocking');
    onBlockedChange(blocked);
  }, [data, onBlockedChange]);

  // Retry = re-fetch from source
  const handleRetry = async () => {
    setIsRetrying(true);
    await fetchData();
    setIsRetrying(false);
  };

  // Save overrides via API and update local state
  const [isSavingOverrides, setIsSavingOverrides] = React.useState(false);
  const [overrideError, setOverrideError] = React.useState<string | null>(null);

  const handleApplyOverrides = async () => {
    if (!data) return;

    const overrideEntries = Object.entries(overrideValues)
      .filter(([, value]) => value.trim())
      .map(([field, value]) => ({ fieldName: field, overrideValue: value.trim(), reason: '' }));

    if (overrideEntries.length === 0) return;

    setIsSavingOverrides(true);
    setOverrideError(null);

    try {
      const token = await getToken();
      const res = await fetch(PBTApiEndpoints.SaveEmployeeDataOverrides, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pbtCaseId, overrides: overrideEntries }),
      });

      if (!res.ok) throw new Error(`Failed to save overrides (${res.status})`);

      const updatedData = { ...data };
      const updatedCompleteness = { ...data.fieldCompleteness };

      overrideEntries.forEach(({ fieldName, overrideValue }) => {
        if (fieldName === 'WorkState') updatedData.workState = overrideValue;
        if (fieldName === 'HomeAddress') updatedData.homeAddress = overrideValue;
        if (fieldName === 'ImmigrationStatus') updatedData.immigrationStatus = overrideValue;
        if (fieldName === 'VisaType') updatedData.visaType = overrideValue;
        updatedCompleteness[fieldName] = 'Complete';
      });

      updatedData.fieldCompleteness = updatedCompleteness;
      setData(updatedData);
      updateCaseInStore(pbtCaseId, { dataStatus: deriveDataStatus(updatedCompleteness) });
      setShowOverrides(false);
      setOverrideValues({});
    } catch (err: any) {
      setOverrideError(err.message);
    } finally {
      setIsSavingOverrides(false);
    }
  };

  if (isLoading) {
    return <Spinner size={SpinnerSize.medium} label="Loading employee data..." />;
  }

  if (error) {
    return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
  }

  if (!data) {
    return <Text>No employee data found.</Text>;
  }

  // Derive blocking/warning from fieldCompleteness. Personal Email is intentionally excluded from
  // the Employee Data tab (bug 16452564): it no longer contributes to data-completeness, blocking,
  // warning, or override logic even if the backend still reports it in fieldCompleteness.
  const fc: FieldCompleteness = Object.fromEntries(
    Object.entries(data.fieldCompleteness || {}).filter(([field]) => field !== 'PersonalEmail')
  );
  const blockingFields = Object.entries(fc)
    .filter(([, status]) => status === 'Blocking')
    .map(([field]) => ({ fieldName: field, message: fieldMessages[field]?.blocking || `${field} is missing (blocking).`, impact: fieldMessages[field]?.impact }));
  const warningFields = Object.entries(fc)
    .filter(([, status]) => status === 'Warning')
    .map(([field]) => ({ fieldName: field, message: fieldMessages[field]?.warning || `${field} is missing (warning).`, impact: fieldMessages[field]?.impact }));

  const overridableFields = [...blockingFields, ...warningFields].filter(f =>
    ['WorkState', 'HomeAddress', 'ImmigrationStatus', 'VisaType'].includes(f.fieldName)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {blockingFields.length > 0 && (
        <div className={caseDetailStyles.blockingBanner}>
          <div className={caseDetailStyles.blockingTitle}>
            Workflow Blocked — Required Data Missing
          </div>
          {blockingFields.map((field) => (
            <div key={field.fieldName}>
              <strong>{field.message}</strong>
              {field.impact && <div style={{ fontSize: 12, marginTop: 2 }}>{field.impact}</div>}
            </div>
          ))}
          <div className={caseDetailStyles.blockingActions}>
            <button
              className={caseDetailStyles.retryButton}
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : 'Retry Pull from Source'}
            </button>
            {overridableFields.length > 0 && (
              <button
                className={caseDetailStyles.overrideButton}
                onClick={() => setShowOverrides(true)}
              >
                Enter Manual Override
              </button>
            )}
          </div>
        </div>
      )}

      {(warningFields.length > 0 || (!isManager && gerWitCheckStatus)) && (
        <div className={caseDetailStyles.warningBanner}>
          <div className={caseDetailStyles.warningTitle}>
            {warningFields.length > 0
              ? 'Missing Fields — Workflow Will Continue'
              : 'GER/WIT Activity Detected'}
          </div>
          <ul className={caseDetailStyles.warningList}>
            {warningFields.map((field) => (
              <li key={field.fieldName}>
                <strong>{field.fieldName}</strong>: {field.message}
                {field.impact && <span style={{ color: '#78350f' }}> — {field.impact}</span>}
              </li>
            ))}
            {!isManager && gerWitCheckStatus && (
              <li>
                <strong>GER/WIT Status</strong>: {gerWitCheckStatus}
              </li>
            )}
          </ul>
          {overridableFields.length > 0 && !showOverrides && blockingFields.length === 0 && (
            <button
              className={caseDetailStyles.overrideButton}
              style={{ marginTop: 10 }}
              onClick={() => setShowOverrides(true)}
            >
              Add Override Values
            </button>
          )}
        </div>
      )}

      {showOverrides && (
        <div className={caseDetailStyles.overrideSection}>
          <div className={caseDetailStyles.overrideTitle}>Manual Override</div>
          {overrideError && (
            <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 8 } }}>{overrideError}</MessageBar>
          )}
          {overridableFields.map((field) => {
            const label = overrideFieldLabels[field.fieldName] || field.fieldName;
            const dropdownOptions = dropdownOptionsByField[field.fieldName];
            return (
              <div key={field.fieldName} className={caseDetailStyles.overrideField}>
                <span className={caseDetailStyles.overrideLabel}>{label}</span>
                {dropdownOptions ? (
                  searchableOverrideFields.has(field.fieldName) ? (
                    <ComboBox
                      className={caseDetailStyles.overrideInput}
                      placeholder={`Search ${label}...`}
                      ariaLabel={label}
                      options={dropdownOptions as IComboBoxOption[]}
                      selectedKey={overrideValues[field.fieldName] || null}
                      allowFreeform={false}
                      autoComplete="on"
                      useComboBoxAsMenuWidth
                      onChange={(_: React.FormEvent<IComboBox>, option?: IComboBoxOption) => setOverrideValues(prev => ({
                        ...prev,
                        [field.fieldName]: option?.key as string ?? '',
                      }))}
                    />
                  ) : (
                    <Dropdown
                      className={caseDetailStyles.overrideInput}
                      placeholder={`Select ${label}...`}
                      ariaLabel={label}
                      options={dropdownOptions as IDropdownOption[]}
                      selectedKey={overrideValues[field.fieldName] || null}
                      onChange={(_, option?: IDropdownOption) => setOverrideValues(prev => ({
                        ...prev,
                        [field.fieldName]: option?.key as string ?? '',
                      }))}
                    />
                  )
                ) : (
                  <input
                    className={caseDetailStyles.overrideInput}
                    placeholder={`Enter ${label}...`}
                    value={overrideValues[field.fieldName] || ''}
                    onChange={(e) => setOverrideValues(prev => ({
                      ...prev,
                      [field.fieldName]: e.target.value,
                    }))}
                  />
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              className={caseDetailStyles.submitOverrideButton}
              onClick={handleApplyOverrides}
              disabled={isSavingOverrides}
            >
              {isSavingOverrides ? 'Saving...' : 'Save Overrides'}
            </button>
            <button
              type="button"
              className={caseDetailStyles.cancelButton}
              onClick={() => { setShowOverrides(false); setOverrideValues({}); setOverrideError(null); }}
              disabled={isSavingOverrides}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {data.immigrationStatus && data.immigrationStatus !== 'No' && (
        <GatedField capability={`${EMP_CAP}/field.emp.immigration`}>
          {({ readOnly }) => (readOnly ? null : (
            <div className={caseDetailStyles.immigrationBanner}>
              Employee has immigration status: <strong>{data.immigrationStatus}{data.visaType ? ` - ${data.visaType}` : ''}</strong> — additional review may be required.
            </div>
          ))}
        </GatedField>
      )}

      {data.sevenDayRule && data.workState && (
        <div className={caseDetailStyles.sevenDayBanner}>
          7-Day Rule applies for this employee&apos;s work state ({data.workState}).
        </div>
      )}

      {!data.hrdpRetrievalSuccess ? (
        <MessageBar messageBarType={MessageBarType.error}>
          We&apos;re unable to fetch the employee&apos;s details right now. Please try again later.
        </MessageBar>
      ) : !data.mimsRetrievalSuccess ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          We&apos;re unable to retrieve immigration information right now. Please try again later.
        </MessageBar>
      ) : null}

      <div className={caseDetailStyles.dataTable}>
        <div className={caseDetailStyles.dataRow}>
          <span className={caseDetailStyles.dataLabel}>Work Address</span>
          <span className={caseDetailStyles.dataValue}>{data.workAddress || '—'}</span>
          <span className={caseDetailStyles.dataLabel}>Home Address</span>
          <GatedField capability={`${EMP_CAP}/field.emp.homeAddr`} fallback={<span className={caseDetailStyles.dataValue}>—</span>}>
            {({ readOnly }) => (
              <span className={caseDetailStyles.dataValue}>{readOnly ? maskValue(data.homeAddress) : (data.homeAddress || '—')}</span>
            )}
          </GatedField>
        </div>
        <div className={caseDetailStyles.dataRow}>
          <span className={caseDetailStyles.dataLabel}>Work State</span>
          <span className={caseDetailStyles.dataValue}>
            {data.workState || <span style={{ color: '#dc2626', fontWeight: 600 }}>MISSING (Blocking)</span>}
          </span>
          <span className={caseDetailStyles.dataLabel}>Home State</span>
          <span className={caseDetailStyles.dataValue}>{data.homeState || '—'}</span>
        </div>
        <div className={caseDetailStyles.dataRow}>
          <span className={caseDetailStyles.dataLabel}>Immigration Status</span>
          <GatedField capability={`${EMP_CAP}/field.emp.immigration`} fallback={<span className={caseDetailStyles.dataValue}>—</span>}>
            {({ readOnly }) => (
              <span className={caseDetailStyles.dataValue}>
                {readOnly
                  ? maskValue(data.immigrationStatus)
                  : (data.immigrationStatus || '—')}
              </span>
            )}
          </GatedField>
          <span className={caseDetailStyles.dataLabel}>Visa Type</span>
          <GatedField capability={`${EMP_CAP}/field.emp.immigration`} fallback={<span className={caseDetailStyles.dataValue}>—</span>}>
            {({ readOnly }) => (
              <span className={caseDetailStyles.dataValue}>
                {readOnly ? maskValue(data.visaType) : (data.visaType || '—')}
              </span>
            )}
          </GatedField>
        </div>
        <div className={caseDetailStyles.dataRow}>
          <span className={caseDetailStyles.dataLabel}>7-Day Rule</span>
          <span className={caseDetailStyles.dataValue}>
            {data.sevenDayRule
              ? <span style={{ color: '#dc2626', fontWeight: 600 }}>Yes — Applies</span>
              : 'No'}
          </span>
          <span className={caseDetailStyles.dataLabel}></span>
          <span className={caseDetailStyles.dataValue}></span>
        </div>
      </div>

      <Text styles={{ root: { fontSize: 12, color: '#9ca3af', marginTop: 8 } }}>
        Retrieved at: {new Date(data.retrievedAt).toLocaleString()}
      </Text>
    </div>
  );
};
